import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../api";
import { useMe } from "../hooks/useMe";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  X,
  Ambulance,
  Upload,
  Download,
} from "lucide-react";
import { downloadCsv } from "../lib/checklistCsv";
import { parseCsv } from "../lib/csv";

export function AmbulancesPage() {
  const { me: meRaw, isLoading } = useMe();
  const me = isLoading ? undefined : meRaw;
  const api = useApi();
  const qc = useQueryClient();
  const ambQ = useQuery({ queryKey: ["ambulances"], queryFn: () => api.get<{ id: string; number: string }[]>("/api/v1/ambulances"), enabled: Boolean(api.token) });
  const ambulances = ambQ.data?.map((a) => ({ ...a, _id: a.id }));
  const inv = () => qc.invalidateQueries({ queryKey: ["ambulances"] });
  const createAmbulance = async ({ number }: { number: string }) => { await api.post("/api/v1/ambulances", { number }); await inv(); };
  const updateAmbulance = async ({ id, number }: { id: string; number: string }) => { await api.patch(`/api/v1/ambulances/${id}`, { number }); await inv(); };
  const removeAmbulance = async ({ id }: { id: string }) => { await api.del(`/api/v1/ambulances/${id}`); await inv(); };
  const importAmbulances = async ({ numbers }: { numbers: string[] }) => { const r = await api.post<{ created: number; skipped: number }>("/api/v1/ambulances/bulk", { numbers }); await inv(); return r; };

  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<{
    _id: string;
    number: string;
  } | null>(null);
  const [number, setNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (me === undefined) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--hmg-red)]" />
      </div>
    );
  }
  if (!me || me.role !== "super_admin") {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-6 text-red-700">
        You need Super Admin access to view this page.
      </div>
    );
  }

  function openCreate() {
    setEditing(null);
    setNumber("");
    setError(null);
    setShowForm(true);
  }
  function openEdit(a: { _id: string; number: string }) {
    setEditing(a);
    setNumber(a.number);
    setError(null);
    setShowForm(true);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (editing) {
        await updateAmbulance({ id: editing._id, number });
      } else {
        await createAmbulance({ number });
      }
      setShowForm(false);
    } catch (err: any) {
      setError(err?.message ?? "Failed to save ambulance");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Ambulances</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-4 py-2 inline-flex items-center gap-2"
          >
            <Upload className="w-4 h-4" /> Bulk import
          </button>
          <button
            onClick={openCreate}
            className="rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white text-sm font-semibold px-4 py-2 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Ambulance
          </button>
        </div>
      </div>

      <p className="text-sm text-slate-600 mb-4">
        These numbers populate the &ldquo;Ambulance No&rdquo; dropdown on the
        checklist form.
      </p>

      {ambulances === undefined ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--hmg-red)]" />
        </div>
      ) : ambulances.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-600">
          No ambulances yet. Add one to get started.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                  <th className="px-4 py-3">Ambulance No</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ambulances.map((a) => (
                  <tr key={a._id}>
                    <td className="px-4 py-3 font-medium text-slate-900 inline-flex items-center gap-2">
                      <Ambulance className="w-4 h-4 text-slate-400" />
                      {a.number}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(a)}
                        className="text-slate-600 hover:text-slate-900 p-1.5 rounded"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={async () => {
                          if (confirm(`Delete ambulance "${a.number}"?`)) {
                            await removeAmbulance({ id: a._id });
                          }
                        }}
                        className="text-red-600 hover:text-red-700 p-1.5 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {editing ? "Edit ambulance" : "Add ambulance"}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Ambulance No *
                </label>
                <input
                  required
                  autoFocus
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  placeholder="e.g. 2162"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
                />
              </div>
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-2 rounded">
                  {error}
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white font-semibold py-2 disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editing ? "Save" : "Create"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImport && (
        <BulkImportModal
          onClose={() => setShowImport(false)}
          runImport={(numbers) => importAmbulances({ numbers })}
        />
      )}
    </div>
  );
}

function BulkImportModal({
  onClose,
  runImport,
}: {
  onClose: () => void;
  runImport: (
    numbers: string[]
  ) => Promise<{ created: number; skipped: number }>;
}) {
  const [parsed, setParsed] = useState<string[] | null>(null);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(
    null
  );

  function downloadTemplate() {
    const csv = "Number\n2162\n7869\n";
    downloadCsv("ambulances-template.csv", csv);
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null);
    setResult(null);
    setParsed(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      const rows = parseCsv(await file.text()).filter((r) =>
        r.some((c) => c.trim() !== "")
      );
      if (rows.length < 1) throw new Error("The file looks empty.");
      const header = rows[0].map((h) => h.trim().toLowerCase());
      // Accept "Number" header; fall back to the first column if it's missing.
      let numIdx = header.indexOf("number");
      let dataRows = rows.slice(1);
      if (numIdx === -1) {
        numIdx = 0;
        dataRows = rows; // no recognizable header — treat every row as data
      }
      const list = dataRows
        .map((r) => (r[numIdx] ?? "").trim())
        .filter((n) => n !== "");
      if (list.length === 0) {
        throw new Error("No ambulance numbers were found.");
      }
      setParsed(list);
    } catch (err: any) {
      setError(err?.message ?? "Could not read the file.");
    }
  }

  async function doImport() {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await runImport(parsed));
    } catch (err: any) {
      setError(err?.message ?? "Import failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold inline-flex items-center gap-2">
            <Upload className="w-5 h-5" /> Bulk import ambulances
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {result ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3">
              Added <span className="font-semibold">{result.created}</span>{" "}
              ambulance{result.created === 1 ? "" : "s"}.
              {result.skipped > 0 && (
                <>
                  {" "}
                  Skipped <span className="font-semibold">{result.skipped}</span>{" "}
                  (blank or duplicate number).
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-full rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <ol className="text-sm text-slate-600 list-decimal pl-5 space-y-1">
              <li>Download the template.</li>
              <li>
                Put one ambulance number per row under the{" "}
                <strong>Number</strong> column.
              </li>
              <li>Save as CSV and upload it below.</li>
            </ol>

            <button
              onClick={downloadTemplate}
              className="w-full rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold py-2 inline-flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" /> Download template (CSV)
            </button>

            <label className="block">
              <span className="block text-sm font-medium text-slate-700 mb-1">
                Upload filled CSV
              </span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={onFile}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-semibold hover:file:bg-slate-200"
              />
            </label>

            {parsed && !error && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm p-3">
                <span className="font-semibold">{parsed.length}</span> number
                {parsed.length === 1 ? "" : "s"} found in{" "}
                <span className="font-mono">{fileName}</span>, ready to import.
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3">
                {error}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button
                onClick={doImport}
                disabled={busy || !parsed}
                className="flex-1 rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white font-semibold py-2 disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Import{parsed ? ` ${parsed.length}` : ""}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
